/**
 * Scanner Status Widget
 * Phase 11: Real-Time WebSocket Integration
 * 
 * WHY: The Operational Cyber Lead mandated real-time scanner status.
 * This widget now uses WebSockets for instant updates instead of polling.
 * 
 * Changes from Phase 7:
 * - Added WebSocket connection for real-time updates
 * - Merged REST API data with WebSocket updates
 * - Connection status indicator
 * - Graceful fallback to polling if WebSocket unavailable
 * 
 * @see .docs/architecture/phase10_deployment_assessment.md - Expert 3 Mandate
 */

import { useState, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type Scanner, type ScannerStatus } from '@/api'
import { useWebSocket, type WebSocketMessage } from '@/hooks/useWebSocket'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Scan,
  AlertCircle,
  CheckCircle,
  XCircle,
  AlertTriangle,
  HelpCircle,
  Clock,
  Wifi,
  WifiOff,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface ScannerStatusWidgetProps {
  className?: string
}

/**
 * Get status display properties.
 */
function getStatusDisplay(status: ScannerStatus) {
  switch (status) {
    case 'online':
      return {
        icon: CheckCircle,
        color: 'text-green-500',
        bgColor: 'bg-green-500',
        label: 'Online',
        variant: 'default' as const,
      }
    case 'offline':
      return {
        icon: XCircle,
        color: 'text-red-500',
        bgColor: 'bg-red-500',
        label: 'Offline',
        variant: 'destructive' as const,
      }
    case 'degraded':
      return {
        icon: AlertTriangle,
        color: 'text-yellow-500',
        bgColor: 'bg-yellow-500',
        label: 'Degraded',
        variant: 'secondary' as const,
      }
    default:
      return {
        icon: HelpCircle,
        color: 'text-muted-foreground',
        bgColor: 'bg-muted-foreground',
        label: 'Unknown',
        variant: 'outline' as const,
      }
  }
}

/**
 * Get scanner type display label.
 */
function getScannerTypeLabel(type: string): string {
  const typeLabels: Record<string, string> = {
    sast: 'SAST',
    dast: 'DAST',
    sca: 'SCA',
    container: 'Container',
    secrets: 'Secrets',
    custom: 'Custom',
  }
  return typeLabels[type] || type.toUpperCase()
}

/**
 * Format relative time for last scan.
 */
function formatLastScan(timestamp?: string): string {
  if (!timestamp) return 'Never'
  
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * Individual scanner row display.
 */
function ScannerRow({ scanner }: { scanner: Scanner }) {
  const statusDisplay = getStatusDisplay(scanner.status)
  const StatusIcon = statusDisplay.icon

  return (
    <div className="flex items-center justify-between py-3 border-b border-border last:border-0">
      <div className="flex items-center gap-3">
        {/* Status indicator */}
        <div className="relative">
          <div className={cn('w-2.5 h-2.5 rounded-full', statusDisplay.bgColor)} />
          {scanner.status === 'online' && (
            <div className={cn('absolute inset-0 w-2.5 h-2.5 rounded-full animate-ping', statusDisplay.bgColor, 'opacity-50')} />
          )}
        </div>
        
        {/* Scanner info */}
        <div>
          <p className="font-medium text-sm">{scanner.name}</p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="outline" className="text-xs px-1.5 py-0">
              {getScannerTypeLabel(scanner.scanner_type)}
            </Badge>
            {scanner.version && <span>v{scanner.version}</span>}
          </div>
        </div>
      </div>

      {/* Right side: status and last scan */}
      <div className="flex items-center gap-4">
        <div className="text-right text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            <span>{formatLastScan(scanner.last_scan_at)}</span>
          </div>
        </div>
        <StatusIcon className={cn('h-4 w-4', statusDisplay.color)} />
      </div>
    </div>
  )
}

/**
 * WebSocket connection status indicator.
 */
function ConnectionIndicator({ isConnected }: { isConnected: boolean }) {
  return (
    <div 
      className={cn(
        'flex items-center gap-1 text-xs',
        isConnected ? 'text-green-500' : 'text-muted-foreground'
      )}
      title={isConnected ? 'Real-time updates active' : 'Polling mode (WebSocket disconnected)'}
    >
      {isConnected ? (
        <>
          <Wifi className="h-3 w-3" />
          <span className="hidden sm:inline">Live</span>
        </>
      ) : (
        <>
          <WifiOff className="h-3 w-3" />
          <span className="hidden sm:inline">Polling</span>
        </>
      )}
    </div>
  )
}

export function ScannerStatusWidget({ className }: ScannerStatusWidgetProps) {
  const queryClient = useQueryClient()
  
  // Local state for optimistic updates from WebSocket
  const [localScanners, setLocalScanners] = useState<Map<string, Scanner>>(new Map())
  
  /**
   * Handle incoming WebSocket messages.
   * WHY: Updates local state immediately for real-time feel,
   * then invalidates query to ensure eventual consistency.
   */
  const handleWebSocketMessage = useCallback((message: WebSocketMessage) => {
    if (message.type === 'scanner_update' && message.data) {
      const scannerData = message.data as Scanner
      
      // Update local state immediately (optimistic update)
      setLocalScanners(prev => {
        const next = new Map(prev)
        next.set(scannerData.scanner_id, scannerData)
        return next
      })
      
      // Also invalidate the query to trigger a refetch (ensures consistency)
      queryClient.invalidateQueries({ queryKey: ['softwareiq', 'scanners'] })
    }
  }, [queryClient])
  
  // WebSocket connection for real-time updates
  const { isConnected } = useWebSocket({
    url: '/api/v1/ws/scanners',
    onMessage: handleWebSocketMessage,
    reconnect: true,
    maxReconnectAttempts: 0, // Infinite reconnect (DoD networks are flaky)
  })
  
  // Initial data fetch via REST API
  const { data: apiScanners, isLoading, error } = useQuery({
    queryKey: ['softwareiq', 'scanners'],
    queryFn: () => api.softwareiq.scanners.list(),
    staleTime: isConnected ? 60_000 : 30_000, // Longer stale time when WS connected
    refetchInterval: isConnected ? false : 60_000, // Only poll if WS disconnected
  })
  
  // Merge API data with WebSocket updates
  const scanners = apiScanners?.map(scanner => {
    // If we have a more recent WebSocket update, use that
    const wsScanner = localScanners.get(scanner.scanner_id)
    if (wsScanner) {
      // Compare timestamps to use the most recent
      const wsTime = wsScanner.last_heartbeat_at ? new Date(wsScanner.last_heartbeat_at).getTime() : 0
      const apiTime = scanner.last_heartbeat_at ? new Date(scanner.last_heartbeat_at).getTime() : 0
      if (wsTime > apiTime) {
        return wsScanner
      }
    }
    return scanner
  }) || []

  // Calculate summary stats
  const onlineCount = scanners.filter(s => s.status === 'online').length
  const totalCount = scanners.length

  if (error) {
    return (
      <Card className={cn('border-destructive/50', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            Error Loading Scanners
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'Failed to load scanner status'}
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Scan className="h-5 w-5 text-primary" />
              Scanner Status
            </CardTitle>
            <CardDescription>Connected security scanners</CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <ConnectionIndicator isConnected={isConnected} />
            {!isLoading && scanners.length > 0 && (
              <Badge
                variant={onlineCount === totalCount ? 'default' : 'secondary'}
                className="text-xs"
              >
                {onlineCount}/{totalCount} Online
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-3 w-3 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : scanners.length > 0 ? (
          <div className="max-h-[300px] overflow-y-auto -mx-4 px-4">
            {scanners.map((scanner) => (
              <ScannerRow key={scanner.scanner_id} scanner={scanner} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Scan className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No scanners registered</p>
            <p className="text-xs mt-1">Configure scanners in the admin panel</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
